import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _isArray from "lodash/isArray";
import _get from "lodash/get";
import { ENTITY_TYPES, PRODUCT_TYPES } from '../../constants';
import { isLegacyHomeownershipEntity } from '../../utils/mortgages';
/**
 * Get the entity type of a MarketplaceEntity.
 *
 * @param {Object} marketplaceEntity Marketplace entity.
 *
 * @return {string} Entity type.
 */

export var getEntityType = function getEntityType(marketplaceEntity) {
  /**
   * The mortgages entity from Homeownership is a special snowflake.
   *
   * Note: `{ raw: true }` must be included in `POST` to Homeownership service to get `type`.
   */
  var entityType = _get(marketplaceEntity, 'entity_type');

  if (!entityType && isLegacyHomeownershipEntity(marketplaceEntity)) {
    return ENTITY_TYPES.PRODUCTS;
  }

  return entityType;
};
/**
 * Map a marketplaceEntity to a function by entity type and call that function
 *
 * @param {Object | Array} args Either the marketplace entity or an
 *     array of arguments to be passed to the selector function. The
 *     first element of the array must be the marketplace entity
 * @param {Object} mappings Map from entity type to function.
 * @return {any} The value of the called mapped function.
 */

export var callSelectorByEntityType = function callSelectorByEntityType(args) {
  var mappings = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

  if (!_isArray(args)) {
    // eslint-disable-next-line no-param-reassign -- directive added automatically by Shepherd migration
    args = [args];
  }

  var _args = args,
      _args2 = _slicedToArray(_args, 1),
      marketplaceEntity = _args2[0];

  var entityType = getEntityType(marketplaceEntity);
  var func = mappings[entityType];

  if (!func) {
    return undefined;
  }

  return func.apply(void 0, _toConsumableArray(args));
};
/**
 * Gets the ancestor product.
 *
 * @param {Object} marketplaceEntity The marketplace entity.
 *
 * @return {string} The ancestor product.
 */

export var getProduct = function getProduct(marketplaceEntity) {
  var _callSelectorByEntity;

  return callSelectorByEntityType(marketplaceEntity, (_callSelectorByEntity = {}, _defineProperty(_callSelectorByEntity, ENTITY_TYPES.OFFERS, function () {
    return marketplaceEntity.product;
  }), _defineProperty(_callSelectorByEntity, ENTITY_TYPES.PRODUCTS, function () {
    return marketplaceEntity;
  }), _callSelectorByEntity));
};
/**
 * Get the ancestor institution.
 *
 * @param {Object} marketplaceEntity The marketplace entity.
 *
 * @return {string} The ancestor institution.
 */

export var getInstitution = function getInstitution(marketplaceEntity) {
  var _callSelectorByEntity2;

  return callSelectorByEntityType(marketplaceEntity, (_callSelectorByEntity2 = {}, _defineProperty(_callSelectorByEntity2, ENTITY_TYPES.OFFERS, function () {
    return marketplaceEntity.product.institution;
  }), _defineProperty(_callSelectorByEntity2, ENTITY_TYPES.PRODUCTS, function () {
    return marketplaceEntity.institution;
  }), _defineProperty(_callSelectorByEntity2, ENTITY_TYPES.INSTITUTIONS, function () {
    return marketplaceEntity;
  }), _callSelectorByEntity2));
};
/**
 * Get the canonical instititution type.
 * @private - Use `getInstitutionType` instead.
 *
 * @param {Object} marketplaceEntity - The marketplace enitty.
 * @return {String} - The canonical institution type.
 */

export var getCanonicalInstitutionType = function getCanonicalInstitutionType(marketplaceEntity) {
  return _get(getInstitution(marketplaceEntity), 'type');
};
/**
 * Get the product type of a MarketplaceEntity.
 *
 * @param  {Object} marketplaceEntity Marketplace entity.
 *
 * @return {string} Product type.
 */

export var getProductType = function getProductType(marketplaceEntity) {
  /**
   * The mortgages entity from Homeownership is a special snowflake.
   *
   * Note: `{ raw: true }` must be included in `POST` to Homeownership service to get `type`.
   */
  if (isLegacyHomeownershipEntity(marketplaceEntity)) {
    return PRODUCT_TYPES.MORTGAGES;
  }

  return _get(marketplaceEntity, 'product_type');
};
/**
 * Map a marketplaceEntity to a function by product type and call that function
 *
 * @param      {Object | Array}  args               Either the marketplace entity or
 *                                                  an array of arguments to be passed to the
 *                                                  selector function. The first element of the
 *                                                  array must be the marketplace entity
 * @param      {Object}           mappings          Map from product type to function
 * @return     {any}  return value of called mapped function
 */

export var callSelectorByProductType = function callSelectorByProductType(args) {
  var mappings = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var defaultFunc = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : function () {
    return undefined;
  };

  if (!_isArray(args)) {
    // eslint-disable-next-line no-param-reassign -- directive added automatically by Shepherd migration
    args = [args];
  }

  var _args3 = args,
      _args4 = _slicedToArray(_args3, 1),
      marketplaceEntity = _args4[0];

  var productType = getProductType(marketplaceEntity);
  var func = mappings[productType];

  if (!func) {
    return defaultFunc.apply(void 0, _toConsumableArray(args));
  }

  return func.apply(void 0, _toConsumableArray(args));
};
/**
 * Map a marketplaceEntity to a function by sub product type and call that function
 *
 * @param      {Object | Array}  args               Either the marketplace entity or
 *                                                  an array of arguments to be passed to the
 *                                                  selector function. The first element of the
 *                                                  array must be the marketplace entity
 * @param      {Object}    subProductTypeMappings   Map from sub product type to function
 * @param      {Object}    productTypeMappings      Map from product type to function
 * @return     {any}  return value of called mapped function
 */

export var callSelectorBySubProductType = function callSelectorBySubProductType(args) {
  var subProductTypeMappings = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var productTypeMappings = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

  if (!_isArray(args)) {
    // eslint-disable-next-line no-param-reassign -- directive added automatically by Shepherd migration
    args = [args];
  }

  var _args5 = args,
      _args6 = _slicedToArray(_args5, 1),
      marketplaceEntity = _args6[0];

  var subProductType = callSelectorByProductType(marketplaceEntity, productTypeMappings);
  var func = subProductTypeMappings[subProductType];
  if (!func) return undefined;
  return func.apply(void 0, _toConsumableArray(args));
};